/**
 * NewsPage — Task #16-FE
 *
 * News-Seite mit API-Anbindung via useNews(). Struktur 1:1 nach
 * v2-final/news.html: eine Card, Zeilen im 90px-Datum/Inhalt-Grid
 * (siehe news.css), kein Tab-Filter (Prototyp kennt keinen).
 */
import { useTranslation } from 'react-i18next'
import { useNews } from './useNews'
import type { NewsItem } from './useNews'
import './news.css'

// Prototyp-Mapping (Network/Mobile/Product/API → pill-primary/accent/success/info).
// Reales Backend liefert zusaetzlich update/feature/maintenance (Schema-Drift,
// siehe NewsPage.test.tsx) — dafuer bleibt die neutrale .pill ohne Modifier.
const TAG_PILL_CLASS: Record<string, string> = {
  network: 'pill-primary',
  mobile: 'pill-accent',
  product: 'pill-success',
  api: 'pill-info',
}

function TagBadge({ item }: { item: NewsItem }) {
  const { t } = useTranslation('news')
  if (!item.tag) return null
  return (
    <span data-testid={`news-tag-${item.id}`} className={`pill ${TAG_PILL_CLASS[item.tag] ?? ''}`}>
      {t(`tags.${item.tag}` as const, item.tag)}
    </span>
  )
}

function NewsRow({ item }: { item: NewsItem }) {
  return (
    <div className="n-item">
      <div className="mono" style={{ fontSize: 12, color: 'var(--text-subtle)' }} data-testid={`news-date-${item.id}`}>
        {item.published_at.slice(0, 10)}
      </div>
      <div>
        <div className="row" style={{ gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 15 }}>{item.title}</strong>
          <TagBadge item={item} />
        </div>
        {item.is_html && item.body_html ? (
          // body_html ist serverseitig per Rails SafeListSanitizer bereinigt,
          // News sind admin-authored (kein User-generierter Inhalt).
          <div
            className="muted n-body-html"
            style={{ fontSize: 13, lineHeight: 1.55 }}
            dangerouslySetInnerHTML={{ __html: item.body_html }}
          />
        ) : (
          <div className="muted" style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-line' }}>
            {item.body}
          </div>
        )}
      </div>
    </div>
  )
}

export function NewsPage() {
  const { data, isLoading, isError } = useNews()
  const { t } = useTranslation('news')

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: '0 0 4px', fontSize: 22 }}>{t('page.title')}</h1>
        <div className="muted" style={{ fontSize: 13 }}>{t('page.subtitle')}</div>
      </div>

      {isLoading && (
        <div className="card card-pad" data-testid="news-loading" style={{ maxWidth: 820 }}>
          <p className="muted">{t('loading')}</p>
        </div>
      )}

      {!isLoading && isError && (
        <div className="card card-pad" data-testid="news-error" style={{ maxWidth: 820 }}>
          <p className="muted">{t('error')}</p>
        </div>
      )}

      {!isLoading && !isError && (
        (data ?? []).length === 0 ? (
          <div className="card card-pad" data-testid="news-empty-state" style={{ maxWidth: 820 }}>
            <p style={{ margin: '0 0 4px' }}>{t('empty.heading')}</p>
            <p className="muted" style={{ margin: 0 }}>{t('empty.body')}</p>
          </div>
        ) : (
          <div className="card card-pad" style={{ maxWidth: 820 }}>
            {(data ?? []).map((item) => (
              <NewsRow key={item.id} item={item} />
            ))}
          </div>
        )
      )}
    </div>
  )
}
